/* ═══════════════════════════════════════════════════════════════════
   RFW FAN CLUBS — Mock Data
   BLOQUE 3 DE 10 — Clubes de Fans
   ═══════════════════════════════════════════════════════════════════ */

export type ClubType = 'oficial_radio' | 'oficial_programa' | 'oficial_locutor' | 'vip' | 'premium';

export const clubTypeLabels: Record<ClubType, string> = {
  oficial_radio: 'Club Oficial de Radio',
  oficial_programa: 'Club Oficial de Programa',
  oficial_locutor: 'Club Oficial de Locutor',
  vip: 'Club VIP',
  premium: 'Club Premium',
};

export const clubTypeIcons: Record<ClubType, string> = {
  oficial_radio: 'ri-radio-line',
  oficial_programa: 'ri-calendar-check-line',
  oficial_locutor: 'ri-mic-line',
  vip: 'ri-vip-crown-line',
  premium: 'ri-vip-diamond-line',
};

export const clubTypeColors: Record<ClubType, { badge: string; dot: string; text: string; bg: string }> = {
  oficial_radio: { badge: 'bg-primary-100 border-primary-300', dot: 'bg-primary-400', text: 'text-primary-700', bg: 'bg-primary-500' },
  oficial_programa: { badge: 'bg-accent-100 border-accent-300', dot: 'bg-accent-400', text: 'text-accent-700', bg: 'bg-accent-500' },
  oficial_locutor: { badge: 'bg-secondary-100 border-secondary-300', dot: 'bg-secondary-400', text: 'text-secondary-700', bg: 'bg-secondary-500' },
  vip: { badge: 'bg-amber-100 border-amber-300', dot: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-500' },
  premium: { badge: 'bg-rose-100 border-rose-300', dot: 'bg-rose-400', text: 'text-rose-700', bg: 'bg-rose-500' },
};

/* ─── Club Challenge ─── */
export interface ClubChallenge {
  id: number;
  title: string;
  description: string;
  participants: number;
  endDate: string;
  reward: string;
  active: boolean;
}

/* ─── Club Post ─── */
export interface ClubPost {
  id: number;
  author: string;
  authorAvatar: string;
  content: string;
  likes: number;
  comments: number;
  timeAgo: string;
  imageUrl?: string;
}

/* ─── Club Member ─── */
export interface ClubMember {
  id: number;
  name: string;
  avatarUrl: string;
  role: 'fundador' | 'moderador' | 'miembro' | 'top_contribuidor';
  joinedDate: string;
  contributions: number;
  score: number;
}

/* ─── Club Activity Item (BLOQUE 26) ─── */
export type ActivityType = 'comentario' | 'like' | 'compartido' | 'reaccion' | 'encuesta' | 'reto';

export interface ClubActivityItem {
  id: number;
  type: ActivityType;
  user: { name: string; avatarUrl: string };
  content: string;
  timestamp: string;
  points: number;
  targetTitle?: string;
}

export const activityTypeConfig: Record<ActivityType, { icon: string; label: string; color: string; bg: string }> = {
  comentario: { icon: 'ri-chat-1-line', label: 'Comentario', color: 'text-sky-600', bg: 'bg-sky-100' },
  like: { icon: 'ri-heart-line', label: 'Like', color: 'text-rose-600', bg: 'bg-rose-100' },
  compartido: { icon: 'ri-share-forward-line', label: 'Compartido', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  reaccion: { icon: 'ri-emotion-happy-line', label: 'Reacción', color: 'text-amber-600', bg: 'bg-amber-100' },
  encuesta: { icon: 'ri-bar-chart-horizontal-line', label: 'Encuesta', color: 'text-violet-600', bg: 'bg-violet-100' },
  reto: { icon: 'ri-sword-line', label: 'Reto', color: 'text-orange-600', bg: 'bg-orange-100' },
};

/* ─── Club Score Action (BLOQUE 26) ─── */
export interface ClubScoreAction {
  key: string;
  label: string;
  icon: string;
  points: number;
  desc: string;
  color: string;
  bg: string;
}

export const clubScoreActions: ClubScoreAction[] = [
  { key: 'unirse', label: 'Entrar al Club', icon: 'ri-user-add-line', points: 100, desc: '+100 pts al unirte a cualquier club oficial RFW', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  { key: 'participar', label: 'Participar', icon: 'ri-chat-1-line', points: 10, desc: '+10 pts por cada comentario, like o reacción dentro del club', color: 'text-sky-600', bg: 'bg-sky-100' },
  { key: 'compartir', label: 'Compartir Club', icon: 'ri-share-forward-line', points: 25, desc: '+25 pts por compartir el club en redes sociales', color: 'text-violet-600', bg: 'bg-violet-100' },
  { key: 'invitar', label: 'Invitar Amigos', icon: 'ri-user-heart-line', points: 50, desc: '+50 pts por cada amigo que se une al club con tu invitación', color: 'text-rose-600', bg: 'bg-rose-100' },
  { key: 'comentar', label: 'Comentar', icon: 'ri-message-2-line', points: 15, desc: '+15 pts por cada comentario con más de 10 likes en el club', color: 'text-amber-600', bg: 'bg-amber-100' },
  { key: 'ganar_retos', label: 'Ganar Retos', icon: 'ri-trophy-line', points: 200, desc: '+200 pts por ganar un reto o desafío del club', color: 'text-orange-600', bg: 'bg-orange-100' },
];

/* ─── Club Button Status (BLOQUE 26) ─── */
export type ClubJoinStatus = 'none' | 'pending' | 'member';

export const clubButtonConfig: Record<ClubJoinStatus, { label: string; icon: string; nextStatus: ClubJoinStatus; nextLabel: string; nextIcon: string }> = {
  none: { label: 'Unirme al Club', icon: 'ri-user-add-line', nextStatus: 'pending', nextLabel: 'Unirme al Club', nextIcon: 'ri-user-add-line' },
  pending: { label: 'Solicitud Enviada', icon: 'ri-time-line', nextStatus: 'member', nextLabel: 'Cancelar Solicitud', nextIcon: 'ri-close-line' },
  member: { label: 'Salir del Club', icon: 'ri-user-unfollow-line', nextStatus: 'none', nextLabel: 'Salir del Club', nextIcon: 'ri-user-unfollow-line' },
};

/* ─── RFW Club ─── */
export interface RfwClub {
  id: number;
  clubName: string;
  type: ClubType;
  /* parent entity */
  parentName: string;
  parentSlug: string;
  parentFrequency?: string;
  parentLogoUrl: string;
  /* description */
  description: string;
  slogan: string;
  /* stats */
  membersCount: number;
  postsCount: number;
  challengesCount: number;
  activeChallengeCount: number;
  eventsCount: number;
  photosCount: number;
  videosCount: number;
  /* club score */
  clubScore: number;
  weeklyScore: number;
  scoreChange: number;
  rankingPosition: number;
  engagementRate: number;
  /* benefits */
  hasBadges: boolean;
  hasEarlyAccess: boolean;
  hasGiveaways: boolean;
  hasRecognition: boolean;
  /* content */
  challenges: ClubChallenge[];
  recentPosts: ClubPost[];
  topMembers: ClubMember[];
  /* dates */
  foundedDate: string;
  /* thumbnail */
  thumbnailUrl: string;
}

/* ═══════════════════════════════════════════════════════════════════
   RADIO OFFICIAL CLUBS — 12 clubs (one per radio)
   ═══════════════════════════════════════════════════════════════════ */

const defaultChallenges: Record<number, ClubChallenge[]> = {
  1: [
    { id: 101, title: 'Comparte tu canción favorita', description: 'Publica un video cantando tu canción favorita de la playlist de esta semana. El más votado gana.', participants: 128, endDate: '2026-06-28', reward: 'Mención en vivo + Insignia', active: true },
    { id: 102, title: 'Reto de Escucha', description: 'Escucha 10 horas de radio esta semana y comparte tu comprobante. Todos ganan puntos.', participants: 256, endDate: '2026-06-25', reward: '100 pts RFW Score', active: true },
  ],
  2: [
    { id: 201, title: 'Cadena de Oración', description: 'Comparte tu intención de oración y ora por otro miembro del club.', participants: 432, endDate: '2026-07-01', reward: 'Insignia Fe', active: true },
    { id: 202, title: 'Versículo del Día', description: 'Comparte una imagen con tu versículo favorito. La más creativa gana.', participants: 189, endDate: '2026-06-27', reward: 'Membresía destacada', active: true },
  ],
  3: [
    { id: 301, title: 'Karaoke Norteño', description: 'Graba un video cantando tu canción norteña favorita. Premio al más auténtico.', participants: 345, endDate: '2026-06-30', reward: 'Saludo en vivo + Playlist dedicada', active: true },
  ],
  5: [
    { id: 501, title: 'Batalla de Bandas', description: 'Vota por tu banda de rock favorita en eliminatorias diarias. La ganadora recibe especial de 2 horas.', participants: 567, endDate: '2026-07-05', reward: 'Especial de radio dedicado', active: true },
  ],
  7: [
    { id: 701, title: 'Predicción Deportiva', description: 'Predice los resultados de la jornada. El que más acierte gana.', participants: 890, endDate: '2026-06-26', reward: 'Camiseta oficial firmada', active: true },
    { id: 702, title: 'Foto con tu equipo', description: 'Comparte una foto con la camiseta de tu equipo. La mejor foto gana.', participants: 456, endDate: '2026-07-02', reward: 'Entradas VIP', active: true },
  ],
  9: [
    { id: 901, title: 'Playlist Colaborativa', description: 'Cada miembro agrega una canción. Creamos la playlist del club.', participants: 234, endDate: '2026-06-29', reward: 'La playlist se transmite al aire', active: true },
  ],
  12: [
    { id: 1201, title: 'Cover de Banda', description: 'Haz tu mejor cover de una canción de banda. El ganador sale al aire.', participants: 445, endDate: '2026-07-03', reward: 'Saludo del locutor + Insignia Estrella', active: true },
    { id: 1202, title: 'Trivia Mexicana', description: 'Responde preguntas sobre música regional mexicana. 10 preguntas, 10 premios.', participants: 312, endDate: '2026-06-28', reward: '200 pts RFW Score', active: true },
  ],
};

function g(id: number): ClubChallenge[] {
  return defaultChallenges[id] || [];
}

const memberPools: Record<number, ClubMember[]> = {
  1: [
    { id: 2001, name: 'Isabella Torres', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20latina%20woman%20in%20radio%20studio%2C%20warm%20smile%2C%20casual%20elegant%2C%20soft%20studio%20lighting%2C%20friendly%20approachable%20expression%2C%20clean%20portrait%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=club-member-1a&orientation=squarish', role: 'fundador', joinedDate: '2025-01-15', contributions: 2840, score: 15200 },
    { id: 2002, name: 'Mateo Vega', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20cheerful%20latino%20man%20in%20his%20late%2020s%2C%20casual%20modern%20look%2C%20friendly%20smile%2C%20clean%20studio%20lighting%2C%20approachable%20vibe%2C%20headshot%20photography%2C%20neutral%20cream%20background&width=200&height=200&seq=club-member-1b&orientation=squarish', role: 'moderador', joinedDate: '2025-03-10', contributions: 1850, score: 9800 },
    { id: 2003, name: 'Valentina Ríos', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20warm%20smiling%20young%20latina%20woman%2C%20natural%20beauty%2C%20casual%20elegant%20look%2C%20soft%20diffused%20studio%20light%2C%20genuine%20expression%2C%20clean%20modern%20headshot%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=club-member-1c&orientation=squarish', role: 'top_contribuidor', joinedDate: '2025-06-20', contributions: 1420, score: 7200 },
  ],
  2: [
    { id: 2004, name: 'Pastor Miguel Ángel', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20warm%20friendly%20latino%20pastor%20in%20his%2040s%2C%20gentle%20smile%2C%20smart%20casual%20attire%2C%20soft%20natural%20lighting%2C%20compassionate%20expression%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=club-member-2a&orientation=squarish', role: 'fundador', joinedDate: '2025-02-01', contributions: 3200, score: 18500 },
    { id: 2005, name: 'Hermana Lucía', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20kind%20latina%20woman%20in%20her%2050s%2C%20warm%20maternal%20smile%2C%20modest%20casual%20attire%2C%20soft%20natural%20light%2C%20gentle%20expression%2C%20clean%20headshot%20photography%2C%20cream%20background&width=200&height=200&seq=club-member-2b&orientation=squarish', role: 'moderador', joinedDate: '2025-04-15', contributions: 2100, score: 11200 },
  ],
  7: [
    { id: 2006, name: 'Carlos "El Capi" Ruiz', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20energetic%20latino%20sports%20commentator%20in%20his%2030s%2C%20passionate%20expression%2C%20casual%20sporty%20look%2C%20dynamic%20studio%20lighting%2C%20enthusiastic%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20dark%20background&width=200&height=200&seq=club-member-7a&orientation=squarish', role: 'fundador', joinedDate: '2025-03-20', contributions: 4500, score: 22800 },
    { id: 2007, name: 'Diego Fernández', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20fit%20latino%20man%20in%20sports%20jersey%2C%20athletic%20build%2C%20confident%20smile%2C%20sporty%20casual%20style%2C%20clean%20studio%20lighting%2C%20energetic%20sports%20fan%20vibe%2C%20headshot%20photography%2C%20neutral%20background&width=200&height=200&seq=club-member-7b&orientation=squarish', role: 'top_contribuidor', joinedDate: '2025-08-10', contributions: 3200, score: 16400 },
  ],
};

function m(id: number): ClubMember[] {
  return memberPools[id] || [
    { id: 9000 + id, name: 'Admin Club', avatarUrl: 'https://readdy.ai/api/search-image?query=Generic%20professional%20portrait%20of%20a%20friendly%20person%2C%20warm%20smile%2C%20casual%20modern%20look%2C%20clean%20studio%20lighting%2C%20approachable%20headshot%20photography%2C%20neutral%20cream%20background&width=200&height=200&seq=club-member-generic&orientation=squarish', role: 'fundador', joinedDate: '2025-06-01', contributions: 1500, score: 8000 },
  ];
}

const postPools: Record<number, ClubPost[]> = {
  1: [
    { id: 3001, author: 'María García', authorAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20cheerful%20young%20latina%20woman%2C%20warm%20genuine%20smile%2C%20casual%20modern%20style%2C%20soft%20natural%20lighting%2C%20friendly%20expression%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=club-post-author-1&orientation=squarish', content: '¡Qué increíble estuvo el programa de hoy! Las canciones que pusieron me hicieron recordar los 90s. ¿Alguien más se emocionó con el playlist?', likes: 87, comments: 23, timeAgo: '2 horas', imageUrl: '' },
    { id: 3002, author: 'Pedro López', authorAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20friendly%20latino%20man%20in%20casual%20hoodie%2C%20relaxed%20smile%2C%20soft%20studio%20lighting%2C%20approachable%20modern%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=club-post-author-2&orientation=squarish', content: 'Comparto mi rincón favorito para escuchar la radio. Una taza de café y la mejor compañía.', likes: 134, comments: 41, timeAgo: '5 horas', imageUrl: 'https://readdy.ai/api/search-image?query=Cozy%20home%20corner%20with%20a%20warm%20cup%20of%20coffee%20next%20to%20a%20vintage%20radio%2C%20soft%20morning%20sunlight%20through%20window%2C%20warm%20amber%20and%20golden%20tones%2C%20comfortable%20peaceful%20atmosphere%2C%20lifestyle%20photography%2C%20clean%20aesthetic%20composition%20with%20natural%20light&width=800&height=600&seq=club-post-img-1&orientation=landscape' },
  ],
  7: [
    { id: 3003, author: 'Juan Hincha', authorAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20excited%20latino%20sports%20fan%20in%20team%20jersey%2C%20energetic%20expression%2C%20casual%20style%2C%20bright%20studio%20lighting%2C%20passionate%20sports%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20background&width=200&height=200&seq=club-post-author-3&orientation=squarish', content: '¡GOOOOOOL! Qué partido el de ayer. Todavía no puedo creer esa jugada del minuto 89. Este equipo va con todo.', likes: 256, comments: 89, timeAgo: '8 horas', imageUrl: '' },
  ],
};

function p(id: number): ClubPost[] {
  return postPools[id] || [];
}

export const radioClubs: RfwClub[] = [
  {
    id: 1,
    clubName: 'Nueva Actitud Fans',
    type: 'oficial_radio',
    parentName: 'Radio Nueva Actitud',
    parentSlug: 'radio-nueva-actitud',
    parentFrequency: '94.70 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20high%20contrast%20vector%20style&width=200&height=200&seq=logo-1&orientation=squarish',
    description: 'El club oficial de Radio Nueva Actitud. Donde los fans se reúnen para compartir su amor por la música pop, participar en retos exclusivos y conectar con los locutores.',
    slogan: 'La comunidad que late al ritmo del pop',
    membersCount: 8450,
    postsCount: 12400,
    challengesCount: 28,
    activeChallengeCount: 2,
    eventsCount: 15,
    photosCount: 890,
    videosCount: 230,
    clubScore: 12500,
    weeklyScore: 1100,
    scoreChange: 200,
    rankingPosition: 4,
    engagementRate: 78,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(1),
    recentPosts: p(1),
    topMembers: m(1),
    foundedDate: '2025-01-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Energetic%20fan%20club%20community%20gathering%20with%20colorful%20pop%20music%20themed%20design%2C%20warm%20vibrant%20atmosphere%20with%20people%20connecting%20through%20music%2C%20modern%20club%20aesthetic%20with%20music%20notes%20and%20equalizer%20visual%20elements%2C%20editorial%20event%20photography%20style%2C%20dynamic%20composition%20with%20warm%20amber%20and%20pink%20tones&width=600&height=400&seq=club-radio-1&orientation=landscape',
  },
  {
    id: 2,
    clubName: 'Comunidad Fe y Esperanza',
    type: 'oficial_radio',
    parentName: 'Radio Fe y Esperanza',
    parentSlug: 'radio-fe-y-esperanza',
    parentFrequency: '87.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio%2C%20isolated%20on%20transparent%20background%2C%20vector%20style%20flat%20design&width=200&height=200&seq=logo-2&orientation=squarish',
    description: 'Un espacio de fe, oración y comunidad. Compartimos testimonios, organizamos cadenas de oración y crecemos juntos en la palabra.',
    slogan: 'Donde la fe se comparte y se multiplica',
    membersCount: 12400,
    postsCount: 18900,
    challengesCount: 35,
    activeChallengeCount: 2,
    eventsCount: 22,
    photosCount: 1200,
    videosCount: 340,
    clubScore: 18500,
    weeklyScore: 1600,
    scoreChange: 350,
    rankingPosition: 2,
    engagementRate: 85,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(2),
    recentPosts: [],
    topMembers: m(2),
    foundedDate: '2025-02-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20spiritual%20community%20gathering%20with%20soft%20golden%20light%2C%20peaceful%20atmosphere%20of%20people%20praying%20together%2C%20dove%20symbolism%2C%20calm%20and%20serene%20club%20aesthetic%2C%20editorial%20photography%20with%20gentle%20warm%20tones%20and%20divine%20light%20rays&width=600&height=400&seq=club-radio-2&orientation=landscape',
  },
  {
    id: 3,
    clubName: 'La Mexicana Fan Club',
    type: 'oficial_radio',
    parentName: 'La Mexicana FM',
    parentSlug: 'la-mexicana-fm',
    parentFrequency: '103.50 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-3&orientation=squarish',
    description: 'El club más fiestero de RFW. Pura banda, norteño y regional mexicano. Concursos de karaoke, retos culturales y la mejor comunidad para los amantes de la música mexicana.',
    slogan: '¡Puro corazón mexicano!',
    membersCount: 18900,
    postsCount: 25600,
    challengesCount: 42,
    activeChallengeCount: 1,
    eventsCount: 28,
    photosCount: 2100,
    videosCount: 560,
    clubScore: 22800,
    weeklyScore: 2100,
    scoreChange: 480,
    rankingPosition: 1,
    engagementRate: 92,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(3),
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-03-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20mexican%20music%20fan%20club%20celebration%20with%20colorful%20papel%20picado%20decorations%2C%20festive%20atmosphere%20with%20warm%20amber%20and%20orange%20tones%2C%20traditional%20and%20modern%20fusion%20aesthetic%2C%20energetic%20community%20party%20vibe%2C%20editorial%20event%20photography%2C%20dynamic%20joyful%20composition&width=600&height=400&seq=club-radio-3&orientation=landscape',
  },
  {
    id: 4,
    clubName: 'Noticias 24/7 Insider',
    type: 'oficial_radio',
    parentName: 'Noticias 24/7 Radio',
    parentSlug: 'noticias-247-radio',
    parentFrequency: '107.90 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%20with%20microphone%20and%20globe%20icon%2C%20circular%20minimalist%20emblem%2C%20blue%20and%20white%20professional%20color%20scheme%2C%20broadcast%20journalism%20style%2C%20isolated%20on%20transparent%20background%2C%20clean%20vector%20design&width=200&height=200&seq=logo-4&orientation=squarish',
    description: 'Debate, análisis y primicias. El club donde los oyentes más informados debaten las noticias del día y comparten sus perspectivas.',
    slogan: 'La verdad en comunidad',
    membersCount: 5200,
    postsCount: 9800,
    challengesCount: 15,
    activeChallengeCount: 0,
    eventsCount: 8,
    photosCount: 340,
    videosCount: 120,
    clubScore: 8900,
    weeklyScore: 650,
    scoreChange: -80,
    rankingPosition: 8,
    engagementRate: 62,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-04-10',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20newsroom%20discussion%20club%20atmosphere%20with%20modern%20media%20monitors%2C%20serious%20intellectual%20debate%20ambiance%2C%20warm%20professional%20lighting%2C%20contemporary%20journalism%20club%20aesthetic%2C%20editorial%20photography%2C%20clean%20composed%20scene%20with%20navy%20and%20warm%20tones&width=600&height=400&seq=club-radio-4&orientation=landscape',
  },
  {
    id: 5,
    clubName: 'Rock Total Legion',
    type: 'oficial_radio',
    parentName: 'Rock Total Radio',
    parentSlug: 'rock-total-radio',
    parentFrequency: '87.01 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-5&orientation=squarish',
    description: 'La legión del rock. Batallas de bandas, trivia de rock clásico, covers y todo lo que un rockero de corazón necesita.',
    slogan: 'El rock no se escucha, se vive',
    membersCount: 11200,
    postsCount: 16800,
    challengesCount: 32,
    activeChallengeCount: 1,
    eventsCount: 18,
    photosCount: 1500,
    videosCount: 420,
    clubScore: 16200,
    weeklyScore: 1500,
    scoreChange: -40,
    rankingPosition: 3,
    engagementRate: 81,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(5),
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-03-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Electric%20rock%20music%20fan%20club%20with%20concert%20atmosphere%2C%20guitar%20silhouettes%20against%20warm%20amber%20stage%20lights%2C%20energetic%20crowd%20vibes%2C%20rock%20and%20roll%20aesthetic%20with%20leather%20and%20denim%20textures%2C%20editorial%20concert%20photography%2C%20dynamic%20dramatic%20composition%20with%20dark%20and%20warm%20tones&width=600&height=400&seq=club-radio-5&orientation=landscape',
  },
  {
    id: 6,
    clubName: 'Comunidad Unida Club',
    type: 'oficial_radio',
    parentName: 'Radio Comunitaria Unida',
    parentSlug: 'radio-comunitaria-unida',
    parentFrequency: '87.02 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-6&orientation=squarish',
    description: 'El club donde la comunidad se organiza. Proyectos locales, voluntariado, eventos barriales y la voz de todos los vecinos.',
    slogan: 'Juntos hacemos comunidad',
    membersCount: 3800,
    postsCount: 7200,
    challengesCount: 18,
    activeChallengeCount: 0,
    eventsCount: 25,
    photosCount: 1800,
    videosCount: 180,
    clubScore: 6500,
    weeklyScore: 480,
    scoreChange: 120,
    rankingPosition: 10,
    engagementRate: 74,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-05-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20community%20gathering%20in%20a%20neighborhood%20setting%2C%20diverse%20people%20connecting%20and%20sharing%2C%20local%20grassroots%20club%20atmosphere%2C%20warm%20natural%20tones%2C%20authentic%20documentary%20photography%20style%2C%20community%20spirit%20with%20earthy%20green%20and%20warm%20amber%20palette&width=600&height=400&seq=club-radio-6&orientation=landscape',
  },
  {
    id: 7,
    clubName: 'Deportes Total Fanáticos',
    type: 'oficial_radio',
    parentName: 'Deportes Total Radio',
    parentSlug: 'deportes-total-radio',
    parentFrequency: '120.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-7&orientation=squarish',
    description: 'El club de los verdaderos fanáticos del deporte. Predicciones, análisis táctico, debates acalorados y la pasión que nos une.',
    slogan: 'Donde la pasión deportiva no tiene límites',
    membersCount: 15600,
    postsCount: 34200,
    challengesCount: 38,
    activeChallengeCount: 2,
    eventsCount: 20,
    photosCount: 2600,
    videosCount: 680,
    clubScore: 19800,
    weeklyScore: 1800,
    scoreChange: 320,
    rankingPosition: 2,
    engagementRate: 89,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(7),
    recentPosts: p(7),
    topMembers: m(7),
    foundedDate: '2025-03-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Passionate%20sports%20fans%20club%20atmosphere%20with%20stadium%20energy%2C%20people%20cheering%20in%20team%20colors%2C%20warm%20golden%20stadium%20floodlights%2C%20dynamic%20sports%20community%20vibe%2C%20editorial%20sports%20photography%2C%20exciting%20competitive%20atmosphere%20with%20dramatic%20lighting%20and%20warm%20tones&width=600&height=400&seq=club-radio-7&orientation=landscape',
  },
  {
    id: 8,
    clubName: 'Voz Global Club',
    type: 'oficial_radio',
    parentName: 'Radio Internacional Voz',
    parentSlug: 'radio-internacional-voz',
    parentFrequency: '140.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20with%20globe%20and%20sound%20waves%2C%20circular%20minimalist%20design%2C%20blue%20and%20teal%20color%20palette%2C%20global%20broadcast%20emblem%2C%20multicultural%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-8&orientation=squarish',
    description: 'Conectando culturas. Un club para los ciudadanos del mundo: intercambio cultural, idiomas, música global y amistades sin fronteras.',
    slogan: 'Sin fronteras, sin límites',
    membersCount: 3100,
    postsCount: 5400,
    challengesCount: 12,
    activeChallengeCount: 0,
    eventsCount: 6,
    photosCount: 480,
    videosCount: 90,
    clubScore: 5400,
    weeklyScore: 350,
    scoreChange: -20,
    rankingPosition: 11,
    engagementRate: 58,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-06-10',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Multicultural%20global%20community%20club%20with%20diverse%20people%20connecting%2C%20world%20map%20visualization%2C%20warm%20international%20atmosphere%2C%20cultural%20exchange%20aesthetic%2C%20editorial%20photography%20with%20rich%20earth%20tones%20and%20global%20travel%20vibe%2C%20peaceful%20harmonious%20composition&width=600&height=400&seq=club-radio-8&orientation=landscape',
  },
  {
    id: 9,
    clubName: 'La Kalle Club',
    type: 'oficial_radio',
    parentName: 'La Kalle 99.9',
    parentSlug: 'la-kalle-999',
    parentFrequency: '99.90 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20purple%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-9&orientation=squarish',
    description: 'El point de encuentro de los que viven la música pop. Playlists colaborativas, baile, challenges virales y la mejor onda.',
    slogan: 'Donde el pop se hace comunidad',
    membersCount: 7200,
    postsCount: 11200,
    challengesCount: 24,
    activeChallengeCount: 1,
    eventsCount: 10,
    photosCount: 980,
    videosCount: 310,
    clubScore: 9800,
    weeklyScore: 850,
    scoreChange: 180,
    rankingPosition: 6,
    engagementRate: 76,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(9),
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-07-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Trendy%20pop%20music%20fan%20club%20with%20neon%20aesthetic%2C%20young%20vibrant%20community%20dancing%20and%20connecting%2C%20warm%20pink%20and%20amber%20lighting%2C%20modern%20club%20atmosphere%20with%20music%20visualizer%20elements%2C%20editorial%20lifestyle%20photography%2C%20energetic%20youthful%20dynamic%20composition&width=600&height=400&seq=club-radio-9&orientation=landscape',
  },
  {
    id: 10,
    clubName: 'Voz del Pueblo Unida',
    type: 'oficial_radio',
    parentName: 'Radio Voz del Pueblo',
    parentSlug: 'radio-voz-del-pueblo',
    parentFrequency: '160.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Local%20community%20radio%20station%20logo%20with%20city%20skyline%20and%20microphone%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20grassroots%20broadcast%20emblem%2C%20local%20charm%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-10&orientation=squarish',
    description: 'La voz de la gente hecha club. Debates comunitarios, proyectos sociales, apoyo mutuo y la fuerza de la solidaridad.',
    slogan: 'El pueblo unido jamás será vencido',
    membersCount: 2800,
    postsCount: 4800,
    challengesCount: 8,
    activeChallengeCount: 0,
    eventsCount: 12,
    photosCount: 640,
    videosCount: 70,
    clubScore: 4200,
    weeklyScore: 280,
    scoreChange: 60,
    rankingPosition: 12,
    engagementRate: 55,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-08-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Grassroots%20community%20club%20gathering%20in%20local%20neighborhood%2C%20authentic%20people%20connecting%20and%20organizing%2C%20warm%20natural%20daylight%2C%20community%20activism%20aesthetic%2C%20documentary%20photography%20style%2C%20earthy%20warm%20tones%20and%20genuine%20human%20connection%20atmosphere&width=600&height=400&seq=club-radio-10&orientation=landscape',
  },
  {
    id: 11,
    clubName: 'Familia Vida Nueva',
    type: 'oficial_radio',
    parentName: 'Radio Vida Nueva',
    parentSlug: 'radio-vida-nueva',
    parentFrequency: '180.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20blue%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-11&orientation=squarish',
    description: 'Una familia de fe que crece unida. Grupos de estudio bíblico, alabanza comunitaria, retiros espirituales y apoyo en todo momento.',
    slogan: 'Crecemos juntos en la fe',
    membersCount: 6800,
    postsCount: 10400,
    challengesCount: 22,
    activeChallengeCount: 0,
    eventsCount: 16,
    photosCount: 850,
    videosCount: 200,
    clubScore: 10500,
    weeklyScore: 920,
    scoreChange: 140,
    rankingPosition: 5,
    engagementRate: 72,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-04-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20faith%20based%20community%20gathering%20with%20soft%20sunrise%20lighting%2C%20people%20sharing%20spiritual%20connection%2C%20peaceful%20worship%20atmosphere%2C%20modern%20church%20community%20aesthetic%2C%20editorial%20photography%20with%20gentle%20golden%20tones%20and%20serene%20composition&width=600&height=400&seq=club-radio-11&orientation=landscape',
  },
  {
    id: 12,
    clubName: 'Banda MX Army',
    type: 'oficial_radio',
    parentName: 'Banda Radio MX',
    parentSlug: 'banda-radio-mx',
    parentFrequency: '200.00 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%2C%20circular%20design%20red%20white%20and%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20traditional%20Mexican%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-12&orientation=squarish',
    description: 'El ejército de la banda más grande de RFW. Retos de canto, batallas de baile, trivia cultural y el orgullo de la música regional.',
    slogan: '¡Puro poder regional!',
    membersCount: 14500,
    postsCount: 22100,
    challengesCount: 36,
    activeChallengeCount: 2,
    eventsCount: 24,
    photosCount: 1900,
    videosCount: 490,
    clubScore: 20500,
    weeklyScore: 1950,
    scoreChange: 560,
    rankingPosition: 1,
    engagementRate: 90,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: g(12),
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-01-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Massive%20mexican%20banda%20music%20fan%20celebration%20with%20festive%20energy%2C%20traditional%20instruments%20silhouette%2C%20vibrant%20warm%20red%20orange%20and%20green%20tones%2C%20crowd%20energy%20at%20concert%2C%20editorial%20event%20photography%2C%20dynamic%20festive%20composition%20with%20dramatic%20cultural%20atmosphere&width=600&height=400&seq=club-radio-12&orientation=landscape',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   PROGRAM OFFICIAL CLUBS — 5 clubs
   ═══════════════════════════════════════════════════════════════════ */

export const programClubs: RfwClub[] = [
  {
    id: 101,
    clubName: 'Tardes con Vale Fans',
    type: 'oficial_programa',
    parentName: 'Tardes con Vale',
    parentSlug: 'tardes-con-vale',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Talk%20show%20radio%20program%20logo%20with%20warm%20sunset%20colors%20and%20microphone%20icon%2C%20circular%20design%20feminine%20approachable%20style%2C%20peach%20and%20coral%20tones%2C%20clean%20modern%20minimalist%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prog-1&orientation=squarish',
    description: 'El rincón favorito de los fans de Vale. Detrás de cámaras, playlist de los viernes, confesiones y el after show exclusivo para miembros.',
    slogan: 'Donde las tardes nunca terminan',
    membersCount: 4500,
    postsCount: 8900,
    challengesCount: 16,
    activeChallengeCount: 1,
    eventsCount: 8,
    photosCount: 520,
    videosCount: 180,
    clubScore: 11500,
    weeklyScore: 950,
    scoreChange: 210,
    rankingPosition: 1,
    engagementRate: 84,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 10001, title: 'Confesión de Tarde', description: 'Comparte tu mejor anécdota de una tarde inolvidable. La más emotiva gana.', participants: 234, endDate: '2026-07-01', reward: 'Mensaje de Vale en vivo', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-09-10',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20afternoon%20talk%20show%20fan%20community%20atmosphere%2C%20cozy%20living%20room%20radio%20listening%20setup%2C%20golden%20hour%20sunset%20lighting%20through%20window%2C%20intimate%20club%20vibe%2C%20editorial%20lifestyle%20photography%2C%20warm%20peach%20and%20coral%20tones&width=600&height=400&seq=club-prog-thumb-1&orientation=landscape',
  },
  {
    id: 102,
    clubName: 'La Comunidad del Desparche',
    type: 'oficial_programa',
    parentName: 'El Desparche',
    parentSlug: 'el-desparche',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Fun%20casual%20radio%20program%20logo%20with%20playful%20cartoon%20elements%2C%20circular%20design%20bright%20energetic%20colors%20orange%20and%20teal%2C%20young%20modern%20style%2C%20clean%20minimalist%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prog-2&orientation=squarish',
    description: 'El espacio más relajado de RFW. Aquí no hay filtros: memes, debates random, challenges absurdos y las mejores risas.',
    slogan: 'Sin filtro, sin drama, puro desparche',
    membersCount: 6200,
    postsCount: 14500,
    challengesCount: 22,
    activeChallengeCount: 2,
    eventsCount: 10,
    photosCount: 780,
    videosCount: 320,
    clubScore: 13800,
    weeklyScore: 1200,
    scoreChange: 380,
    rankingPosition: 1,
    engagementRate: 88,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 10002, title: 'Meme del Día', description: 'Sube tu mejor meme. El más votado aparece en el programa.', participants: 456, endDate: '2026-06-30', reward: 'Shoutout en vivo', active: true },
      { id: 10003, title: 'Historia Random', description: 'Cuenta la historia más random que te haya pasado. Sin filtros.', participants: 312, endDate: '2026-07-05', reward: 'Insignia Leyenda', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-02-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Fun%20casual%20hangout%20club%20atmosphere%20with%20neon%20accents%2C%20young%20diverse%20friends%20laughing%20and%20sharing%20memes%2C%20relaxed%20modern%20space%2C%20warm%20amber%20and%20teal%20accent%20lighting%2C%20lifestyle%20photography%2C%20energetic%20playful%20composition&width=600&height=400&seq=club-prog-thumb-2&orientation=landscape',
  },
  {
    id: 103,
    clubName: 'Creativos Club',
    type: 'oficial_programa',
    parentName: 'Modo Creativo',
    parentSlug: 'modo-creativo',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Creative%20arts%20radio%20program%20logo%20with%20paintbrush%20and%20lightbulb%2C%20circular%20design%20colorful%20warm%20gradient%2C%20artistic%20inspiration%20emblem%2C%20clean%20modern%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prog-3&orientation=squarish',
    description: 'El laboratorio creativo de RFW. Comparte tu arte, recibe feedback, participa en desafíos de diseño y conecta con otros creadores.',
    slogan: 'Donde las ideas toman vuelo',
    membersCount: 3400,
    postsCount: 6200,
    challengesCount: 14,
    activeChallengeCount: 1,
    eventsCount: 5,
    photosCount: 920,
    videosCount: 160,
    clubScore: 8200,
    weeklyScore: 680,
    scoreChange: 90,
    rankingPosition: 2,
    engagementRate: 75,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-11-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Creative%20arts%20community%20workspace%20with%20warm%20ambient%20lighting%2C%20artistic%20supplies%20and%20digital%20screens%2C%20collaborative%20creative%20atmosphere%2C%20modern%20studio%20aesthetic%2C%20editorial%20photography%20with%20warm%20coral%20and%20amber%20tones%2C%20inspiring%20creative%20energy&width=600&height=400&seq=club-prog-thumb-3&orientation=landscape',
  },
  {
    id: 104,
    clubName: 'Deporte Total Comunidad',
    type: 'oficial_programa',
    parentName: 'Deporte Total',
    parentSlug: 'deporte-total',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20talk%20program%20logo%20with%20action%20lines%20and%20ball%20icon%2C%20circular%20design%20energetic%20navy%20and%20gold%20colors%2C%20modern%20sports%20media%20emblem%2C%20clean%20professional%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prog-4&orientation=squarish',
    description: 'Análisis táctico profundo, debates sin censura y la comunidad más futbolera de la plataforma.',
    slogan: 'El deporte se analiza en equipo',
    membersCount: 5200,
    postsCount: 11200,
    challengesCount: 18,
    activeChallengeCount: 0,
    eventsCount: 7,
    photosCount: 650,
    videosCount: 210,
    clubScore: 9600,
    weeklyScore: 780,
    scoreChange: -30,
    rankingPosition: 3,
    engagementRate: 70,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-08-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Sports%20analysis%20fan%20club%20atmosphere%20with%20multiple%20screens%20showing%20match%20highlights%2C%20passionate%20debate%20among%20fans%2C%20warm%20ambient%20lighting%2C%20sports%20bar%20aesthetic%2C%20editorial%20photography%20with%20dynamic%20energy%20and%20navy%20gold%20tones&width=600&height=400&seq=club-prog-thumb-4&orientation=landscape',
  },
  {
    id: 105,
    clubName: 'Mañanas Argentinas Club',
    type: 'oficial_programa',
    parentName: 'Mañanas Argentinas',
    parentSlug: 'mananas-argentinas',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20logo%20with%20sunrise%20and%20coffee%20cup%20icon%2C%20circular%20design%20warm%20amber%20and%20cream%20colors%2C%20cozy%20morning%20broadcast%20emblem%2C%20clean%20minimalist%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prog-5&orientation=squarish',
    description: 'El mate club digital. Actualidad, humor, debates de actualidad y la calidez de las mañanas compartidas.',
    slogan: 'Arrancamos el día juntos',
    membersCount: 3900,
    postsCount: 7800,
    challengesCount: 10,
    activeChallengeCount: 0,
    eventsCount: 4,
    photosCount: 380,
    videosCount: 100,
    clubScore: 7200,
    weeklyScore: 540,
    scoreChange: 40,
    rankingPosition: 4,
    engagementRate: 66,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-07-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Cozy%20morning%20radio%20show%20fan%20community%20with%20coffee%20cups%20and%20sunrise%20warm%20light%2C%20comfortable%20home%20listening%20atmosphere%2C%20golden%20morning%20glow%20through%20windows%2C%20warm%20amber%20and%20cream%20tones%2C%20lifestyle%20photography%2C%20peaceful%20intimate%20composition&width=600&height=400&seq=club-prog-thumb-5&orientation=landscape',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   HOST OFFICIAL CLUBS — 5 clubs
   ═══════════════════════════════════════════════════════════════════ */

export const hostClubs: RfwClub[] = [
  {
    id: 201,
    clubName: 'Ricky Navarro Army',
    type: 'oficial_locutor',
    parentName: 'Ricky Navarro',
    parentSlug: 'ricky-navarro',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20radio%20host%20portrait%20with%20confident%20expression%2C%20modern%20casual%20style%2C%20warm%20studio%20lighting%2C%20energetic%20personality%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background%2C%20editorial%20quality&width=200&height=200&seq=club-host-1&orientation=squarish',
    description: 'El ejército del locutor más carismático de RFW. Contenido exclusivo, saludos personalizados y el backstage de cada transmisión.',
    slogan: 'Ricky no transmite solo, transmite con su Army',
    membersCount: 5800,
    postsCount: 10200,
    challengesCount: 15,
    activeChallengeCount: 1,
    eventsCount: 6,
    photosCount: 480,
    videosCount: 150,
    clubScore: 10500,
    weeklyScore: 920,
    scoreChange: 250,
    rankingPosition: 1,
    engagementRate: 82,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 20001, title: 'Imita a Ricky', description: 'Haz tu mejor imitación del estilo de Ricky. El más auténtico gana.', participants: 178, endDate: '2026-07-02', reward: 'Video mensaje de Ricky', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-10-05',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Energetic%20radio%20host%20fan%20club%20gathering%20with%20charismatic%20personality%20atmosphere%2C%20warm%20studio%20lighting%20vibes%2C%20enthusiastic%20community%20connecting%2C%20modern%20club%20aesthetic%20with%20amber%20and%20warm%20tones%2C%20editorial%20photography%2C%20dynamic%20passionate%20composition&width=600&height=400&seq=club-host-thumb-1&orientation=landscape',
  },
  {
    id: 202,
    clubName: 'Quintero Fans Club',
    type: 'oficial_locutor',
    parentName: 'Diego Quintero',
    parentSlug: 'diego-quintero',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20host%20portrait%20with%20warm%20genuine%20smile%2C%20smart%20casual%20attire%2C%20clean%20studio%20lighting%2C%20approachable%20personality%2C%20headshot%20photography%2C%20neutral%20cream%20background%2C%20editorial%20quality&width=200&height=200&seq=club-host-2&orientation=squarish',
    description: 'La comunidad que sigue a Diego en cada transmisión. Debates, recomendaciones musicales y el after party digital.',
    slogan: 'Quintero y su gente',
    membersCount: 4200,
    postsCount: 7800,
    challengesCount: 12,
    activeChallengeCount: 0,
    eventsCount: 5,
    photosCount: 340,
    videosCount: 110,
    clubScore: 8900,
    weeklyScore: 720,
    scoreChange: 80,
    rankingPosition: 2,
    engagementRate: 74,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-01-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20radio%20host%20community%20club%20atmosphere%20with%20music%20listening%20vibes%2C%20comfortable%20social%20gathering%2C%20modern%20lounge%20aesthetic%2C%20warm%20amber%20lighting%2C%20editorial%20photography%2C%20relaxed%20friendly%20composition&width=600&height=400&seq=club-host-thumb-2&orientation=landscape',
  },
  {
    id: 203,
    clubName: 'Valentina Ríos Fans',
    type: 'oficial_locutor',
    parentName: 'Valentina Ríos',
    parentSlug: 'valentina-rios',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Beautiful%20latina%20radio%20host%20portrait%20with%20warm%20confident%20smile%2C%20elegant%20casual%20style%2C%20soft%20studio%20lighting%2C%20charismatic%20personality%2C%20headshot%20photography%2C%20neutral%20warm%20background%2C%20editorial%20quality&width=200&height=200&seq=club-host-3&orientation=squarish',
    description: 'El espacio de los fans de Vale. Playlists curadas, tips de vida y la energía positiva que solo ella transmite.',
    slogan: 'Brillando juntos con Vale',
    membersCount: 3600,
    postsCount: 6400,
    challengesCount: 10,
    activeChallengeCount: 1,
    eventsCount: 4,
    photosCount: 290,
    videosCount: 90,
    clubScore: 7200,
    weeklyScore: 580,
    scoreChange: 120,
    rankingPosition: 3,
    engagementRate: 71,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-03-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Warm%20feminine%20community%20club%20atmosphere%20with%20soft%20rose%20and%20coral%20tones%2C%20elegant%20social%20gathering%2C%20modern%20cozy%20aesthetic%2C%20gentle%20warm%20lighting%2C%20lifestyle%20photography%2C%20intimate%20friendly%20composition&width=600&height=400&seq=club-host-thumb-3&orientation=landscape',
  },
  {
    id: 204,
    clubName: 'Beto Fuentes Legión',
    type: 'oficial_locutor',
    parentName: 'Beto Fuentes',
    parentSlug: 'beto-fuentes',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Experienced%20radio%20host%20portrait%20with%20distinguished%20look%2C%20salt%20and%20pepper%20hair%2C%20warm%20confident%20smile%2C%20professional%20casual%20style%2C%20rich%20studio%20lighting%2C%20headshot%20photography%2C%20neutral%20dark%20background%2C%20editorial%20quality&width=200&height=200&seq=club-host-4&orientation=squarish',
    description: 'La legión de uno de los locutores más respetados. Anécdotas, sabiduría radial y la escuela de los que aman el micrófono.',
    slogan: 'La vieja escuela nunca muere',
    membersCount: 2900,
    postsCount: 5200,
    challengesCount: 8,
    activeChallengeCount: 0,
    eventsCount: 3,
    photosCount: 210,
    videosCount: 60,
    clubScore: 5800,
    weeklyScore: 420,
    scoreChange: -40,
    rankingPosition: 4,
    engagementRate: 62,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-12-10',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Classic%20radio%20broadcasting%20community%20club%20with%20vintage%20microphone%20and%20warm%20studio%20atmosphere%2C%20experienced%20broadcaster%20vibe%2C%20rich%20amber%20and%20wood%20tones%2C%20editorial%20photography%2C%20nostalgic%20professional%20composition&width=600&height=400&seq=club-host-thumb-4&orientation=landscape',
  },
  {
    id: 205,
    clubName: 'Martín Castellanos Club',
    type: 'oficial_locutor',
    parentName: 'Martín Castellanos',
    parentSlug: 'martin-castellanos',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Young%20modern%20radio%20host%20portrait%20with%20fresh%20energetic%20look%2C%20casual%20streetwear%20style%2C%20bright%20studio%20lighting%2C%20dynamic%20personality%2C%20headshot%20photography%2C%20neutral%20white%20background%2C%20editorial%20quality&width=200&height=200&seq=club-host-5&orientation=squarish',
    description: 'El club del locutor revelación. Música nueva, cultura pop, gaming y la energía de la nueva generación radial.',
    slogan: 'La nueva era de la radio',
    membersCount: 2400,
    postsCount: 4500,
    challengesCount: 6,
    activeChallengeCount: 0,
    eventsCount: 2,
    photosCount: 180,
    videosCount: 50,
    clubScore: 4800,
    weeklyScore: 350,
    scoreChange: 40,
    rankingPosition: 5,
    engagementRate: 59,
    hasBadges: true,
    hasEarlyAccess: false,
    hasGiveaways: false,
    hasRecognition: true,
    challenges: [],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-04-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20young%20radio%20host%20fan%20community%20with%20gaming%20and%20pop%20culture%20aesthetic%2C%20neon%20accent%20lighting%2C%20contemporary%20lounge%20atmosphere%2C%20warm%20amber%20and%20teal%20tones%2C%20lifestyle%20photography%2C%20fresh%20energetic%20composition&width=600&height=400&seq=club-host-thumb-5&orientation=landscape',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   PREMIUM CLUBS — 5 clubs (BLOQUE 26)
   ═══════════════════════════════════════════════════════════════════ */

export const premiumClubs: RfwClub[] = [
  {
    id: 301,
    clubName: 'Élite Select',
    type: 'premium',
    parentName: 'Radio Éxito FM',
    parentSlug: 'radio-exito-fm',
    parentFrequency: '98.5 FM',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Premium%20elite%20radio%20club%20logo%20with%20diamond%20crown%20emblem%2C%20circular%20design%20rose%20gold%20and%20black%20colors%2C%20luxury%20exclusive%20broadcast%20brand%2C%20minimalist%20elegant%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prem-1&orientation=squarish',
    description: 'El club más exclusivo de RFW. Contenido premium curado semanalmente, sesiones privadas con artistas, acceso anticipado a preventas de conciertos y experiencias VIP que ningún otro club ofrece.',
    slogan: 'La excelencia tiene su comunidad',
    membersCount: 1200,
    postsCount: 3800,
    challengesCount: 14,
    activeChallengeCount: 2,
    eventsCount: 6,
    photosCount: 420,
    videosCount: 180,
    clubScore: 14200,
    weeklyScore: 1300,
    scoreChange: 420,
    rankingPosition: 1,
    engagementRate: 94,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 30001, title: 'Sesión Acústica Privada', description: 'Vota por el artista que quieres ver en la próxima sesión acústica exclusiva del club.', participants: 567, endDate: '2026-07-10', reward: 'Acceso a sesión privada + Meet & Greet', active: true },
      { id: 30002, title: 'Curaduría Musical', description: 'Propone canciones para la playlist curada por el club. Las más votadas entran.', participants: 432, endDate: '2026-07-05', reward: 'Playlist con tu nombre + Insignia Curador', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-10-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Luxurious%20exclusive%20club%20atmosphere%20with%20rose%20gold%20accents%20and%20velvet%20textures%2C%20sophisticated%20premium%20members%20gathering%2C%20elegant%20warm%20ambient%20lighting%2C%20high%20end%20private%20club%20aesthetic%20with%20diamond%20and%20crystal%20details%2C%20editorial%20luxury%20lifestyle%20photography%2C%20rich%20warm%20composition%20with%20champagne%20and%20gold%20tones&width=600&height=400&seq=club-prem-thumb-1&orientation=landscape',
  },
  {
    id: 302,
    clubName: 'Deportivo Premium',
    type: 'premium',
    parentName: 'Radio Deportiva Total',
    parentSlug: 'radio-deportiva-total',
    parentFrequency: '95.7 FM',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Premium%20sports%20club%20logo%20with%20gold%20championship%20belt%20emblem%2C%20circular%20design%20navy%20and%20gold%20colors%2C%20elite%20sports%20membership%20brand%2C%20clean%20professional%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prem-2&orientation=squarish',
    description: 'Análisis táctico avanzado con ex profesionales, predicciones exclusivas con data en tiempo real, acceso a palcos VIP en eventos deportivos y contenido que ningún fan regular ve.',
    slogan: 'El deporte desde adentro',
    membersCount: 850,
    postsCount: 5200,
    challengesCount: 18,
    activeChallengeCount: 1,
    eventsCount: 4,
    photosCount: 320,
    videosCount: 210,
    clubScore: 11800,
    weeklyScore: 1050,
    scoreChange: 310,
    rankingPosition: 2,
    engagementRate: 91,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 30003, title: 'Predicción Premium', description: 'Predice marcadores exactos de la jornada. Gana quien más acierte.', participants: 345, endDate: '2026-07-03', reward: 'Palco VIP + Camiseta Firmada', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-11-15',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Premium%20sports%20lounge%20club%20with%20leather%20seating%20and%20multiple%20screens%2C%20sophisticated%20sports%20fans%20in%20upscale%20environment%2C%20warm%20amber%20and%20navy%20ambient%20lighting%2C%20exclusive%20VIP%20sports%20viewing%20atmosphere%2C%20editorial%20luxury%20sports%20photography%2C%20rich%20composition%20with%20gold%20accents&width=600&height=400&seq=club-prem-thumb-2&orientation=landscape',
  },
  {
    id: 303,
    clubName: 'Capital Premium Club',
    type: 'premium',
    parentName: 'Estéreo Capital',
    parentSlug: 'estereo-capital',
    parentFrequency: '102.1 FM',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Elegant%20cultural%20premium%20club%20logo%20with%20art%20deco%20pattern%20emblem%2C%20circular%20design%20cream%20and%20espresso%20colors%2C%20sophisticated%20arts%20membership%20brand%2C%20minimalist%20classic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prem-3&orientation=squarish',
    description: 'Cultura en estado puro. Acceso a galerías privadas, cenas con artistas, colecciones curadas de vinilos, preventas exclusivas de festivales y experiencias gastronómicas de alto nivel.',
    slogan: 'Cultura que transforma',
    membersCount: 620,
    postsCount: 2800,
    challengesCount: 10,
    activeChallengeCount: 1,
    eventsCount: 8,
    photosCount: 560,
    videosCount: 140,
    clubScore: 9600,
    weeklyScore: 880,
    scoreChange: 250,
    rankingPosition: 3,
    engagementRate: 87,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 30004, title: 'Crítica Cultural', description: 'Escribe la mejor reseña del último evento cultural del club.', participants: 189, endDate: '2026-07-08', reward: 'Cena con el artista + Obra firmada', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2025-12-01',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Sophisticated%20cultural%20club%20lounge%20with%20art%20on%20walls%20and%20wine%20tasting%2C%20elegant%20intellectual%20atmosphere%2C%20warm%20cream%20and%20espresso%20ambient%20tones%2C%20exclusive%20cultural%20society%20aesthetic%20with%20books%20and%20classical%20details%2C%20editorial%20luxury%20lifestyle%20photography%2C%20refined%20composition%20with%20soft%20golden%20lighting&width=600&height=400&seq=club-prem-thumb-3&orientation=landscape',
  },
  {
    id: 304,
    clubName: 'Rock Legends Vault',
    type: 'premium',
    parentName: 'Rock Total Radio',
    parentSlug: 'rock-total-radio',
    parentFrequency: '87.01 RFW',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Premium%20rock%20music%20club%20logo%20with%20silver%20skull%20and%20crossbones%20emblem%2C%20circular%20design%20black%20and%20metallic%20colors%2C%20exclusive%20rock%20legacy%20brand%2C%20edgy%20refined%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prem-4&orientation=squarish',
    description: 'La bóveda del rock. Vinilos de colección firmados, sesiones de escucha con ingenieros de sonido legendarios, meet & greets con bandas históricas y memorabilia que ningún fan común puede tocar.',
    slogan: 'La historia del rock en tus manos',
    membersCount: 480,
    postsCount: 2200,
    challengesCount: 8,
    activeChallengeCount: 1,
    eventsCount: 3,
    photosCount: 280,
    videosCount: 160,
    clubScore: 8200,
    weeklyScore: 720,
    scoreChange: 180,
    rankingPosition: 4,
    engagementRate: 83,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 30005, title: 'Vinilo Legendario', description: 'Comparte la historia de tu vinilo más preciado. El mejor relato gana.', participants: 234, endDate: '2026-07-12', reward: 'Vinilo firmado por la banda + Insignia Coleccionista', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-01-10',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exclusive%20rock%20memorabilia%20club%20vault%20with%20signed%20guitars%20and%20vinyl%20records%20on%20dark%20walls%2C%20warm%20amber%20spotlights%20on%20collectibles%2C%20premium%20music%20archive%20aesthetic%20with%20leather%20and%20metal%20textures%2C%20editorial%20luxury%20interior%20photography%2C%20dramatic%20composition%20with%20rich%20dark%20tones&width=600&height=400&seq=club-prem-thumb-4&orientation=landscape',
  },
  {
    id: 305,
    clubName: 'Creadores Premium',
    type: 'premium',
    parentName: 'Radio Juvenil FM',
    parentSlug: 'radio-juvenil-fm',
    parentFrequency: '105.3 FM',
    parentLogoUrl: 'https://readdy.ai/api/search-image?query=Premium%20creator%20club%20logo%20with%20golden%20star%20burst%20emblem%2C%20circular%20design%20vibrant%20coral%20and%20white%20colors%2C%20exclusive%20content%20creator%20brand%2C%20modern%20creative%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=club-prem-5&orientation=squarish',
    description: 'El espacio para creadores de contenido. Acceso a librería de samples premium, mentorías con productores, colaboraciones pagadas con artistas y la comunidad de creadores más top de RFW.',
    slogan: 'Crea sin límites, brilla sin filtros',
    membersCount: 380,
    postsCount: 1900,
    challengesCount: 12,
    activeChallengeCount: 1,
    eventsCount: 5,
    photosCount: 340,
    videosCount: 200,
    clubScore: 7500,
    weeklyScore: 680,
    scoreChange: 290,
    rankingPosition: 5,
    engagementRate: 89,
    hasBadges: true,
    hasEarlyAccess: true,
    hasGiveaways: true,
    hasRecognition: true,
    challenges: [
      { id: 30006, title: 'Remix Challenge', description: 'Haz el mejor remix usando la librería de samples del club.', participants: 178, endDate: '2026-07-15', reward: 'Colaboración con artista RFW + Equipo de producción', active: true },
    ],
    recentPosts: [],
    topMembers: [],
    foundedDate: '2026-02-20',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20club%20with%20LED%20lighting%20and%20production%20equipment%2C%20young%20creators%20collaborating%20in%20neon%20lit%20premium%20space%2C%20warm%20coral%20and%20amber%20ambient%20tones%2C%20exclusive%20content%20creator%20collective%20aesthetic%2C%20editorial%20lifestyle%20photography%2C%20dynamic%20energetic%20composition%20with%20tech%20details&width=600&height=400&seq=club-prem-thumb-5&orientation=landscape',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   ACTIVITY FEED — Global club activity (BLOQUE 26)
   ═══════════════════════════════════════════════════════════════════ */

export const globalClubActivityFeed: ClubActivityItem[] = [
  { id: 4001, type: 'comentario', user: { name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20cheerful%20young%20latina%20woman%2C%20warm%20genuine%20smile%2C%20casual%20modern%20style%2C%20soft%20natural%20lighting%2C%20friendly%20expression%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=act-maria&orientation=squarish' }, content: '¡Qué increíble estuvo el playlist de hoy! Me transportó a los 90s completamente.', timestamp: 'Hace 2 horas', points: 15, targetTitle: 'Nueva Actitud Fans' },
  { id: 4002, type: 'like', user: { name: 'Pedro López', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20friendly%20latino%20man%20in%20casual%20hoodie%2C%20relaxed%20smile%2C%20soft%20studio%20lighting%2C%20approachable%20modern%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=act-pedro&orientation=squarish' }, content: 'Le dio like a la publicación de la playlist semanal.', timestamp: 'Hace 3 horas', points: 5, targetTitle: 'La Kalle Club' },
  { id: 4003, type: 'compartido', user: { name: 'Isabella Torres', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20latina%20woman%2C%20warm%20smile%2C%20casual%20elegant%2C%20soft%20studio%20lighting%2C%20friendly%20expression%2C%20clean%20portrait%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=act-isabella&orientation=squarish' }, content: 'Compartió el club en Instagram Stories. ¡Ya somos 3 más gracias a ella!', timestamp: 'Hace 4 horas', points: 25, targetTitle: 'La Mexicana Fan Club' },
  { id: 4004, type: 'reaccion', user: { name: 'Mateo Vega', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20cheerful%20latino%20man%20in%20his%20late%2020s%2C%20casual%20modern%20look%2C%20friendly%20smile%2C%20clean%20studio%20lighting%2C%20approachable%20vibe%2C%20headshot%20photography%2C%20neutral%20cream%20background&width=200&height=200&seq=act-mateo&orientation=squarish' }, content: 'Reaccionó con 🔥 al reto de la semana.', timestamp: 'Hace 5 horas', points: 3, targetTitle: 'Deportes Total Fanáticos' },
  { id: 4005, type: 'reto', user: { name: 'Valentina Ríos', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20warm%20smiling%20young%20latina%20woman%2C%20natural%20beauty%2C%20casual%20elegant%20look%2C%20soft%20diffused%20studio%20light%2C%20genuine%20expression%2C%20clean%20modern%20headshot%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=act-valentina&orientation=squarish' }, content: 'Completó el Reto de Escucha: 10 horas en una semana. ¡Increíble!', timestamp: 'Hace 6 horas', points: 200, targetTitle: 'Nueva Actitud Fans' },
  { id: 4006, type: 'encuesta', user: { name: 'Felipe Gutiérrez', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latino%20man%20in%20his%20twenties%2C%20charismatic%20smile%2C%20casual%20urban%20style%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-felipe&orientation=squarish' }, content: 'Votó en la encuesta: ¿Cuál es el mejor género de los 80s?', timestamp: 'Hace 7 horas', points: 5, targetTitle: 'Rock Total Legion' },
  { id: 4007, type: 'comentario', user: { name: 'Adriana Mejía', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20in%20her%20twenties%2C%20warm%20genuine%20smile%2C%20elegant%20casual%20style%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-adriana&orientation=squarish' }, content: 'Este club es mi lugar feliz. Gracias a todos por hacer de esto una comunidad tan especial.', timestamp: 'Hace 8 horas', points: 15, targetTitle: 'Comunidad Fe y Esperanza' },
  { id: 4008, type: 'like', user: { name: 'Javier Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latino%20man%20in%20his%20twenties%2C%20sporty%20energetic%20look%2C%20friendly%20smile%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-javier&orientation=squarish' }, content: 'Le dio like a 5 publicaciones del feed del club.', timestamp: 'Hace 9 horas', points: 5, targetTitle: 'Deportes Total Fanáticos' },
  { id: 4009, type: 'compartido', user: { name: 'Camila Restrepo', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20in%20her%20twenties%2C%20bright%20cheerful%20smile%2C%20trendy%20casual%20look%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-camila&orientation=squarish' }, content: 'Compartió el link del club en WhatsApp. 5 amigos nuevos se unieron.', timestamp: 'Hace 10 horas', points: 25, targetTitle: 'Élite Select' },
  { id: 4010, type: 'reto', user: { name: 'Carlos "El Capi" Ruiz', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20energetic%20latino%20sports%20commentator%20in%20his%2030s%2C%20passionate%20expression%2C%20casual%20sporty%20look%2C%20dynamic%20studio%20lighting%2C%20enthusiastic%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20dark%20background&width=200&height=200&seq=act-carlos&orientation=squarish' }, content: '¡Ganó la Predicción Deportiva! 8 de 8 aciertos. Inalcanzable.', timestamp: 'Hace 12 horas', points: 200, targetTitle: 'Deportes Total Fanáticos' },
  { id: 4011, type: 'reaccion', user: { name: 'Daniel Ortega', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latino%20man%20in%20his%20twenties%2C%20relaxed%20smile%2C%20casual%20t-shirt%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-daniel&orientation=squarish' }, content: 'Reaccionó con ❤️ a la foto del evento del club.', timestamp: 'Hace 14 horas', points: 3, targetTitle: 'Rock Legends Vault' },
  { id: 4012, type: 'encuesta', user: { name: 'Sofía Quintero', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20in%20her%20twenties%2C%20warm%20genuine%20smile%2C%20casual%20boho%20style%2C%20clean%20portrait%2C%20editorial%20photography%20with%20soft%20warm%20neutral%20background&width=200&height=200&seq=act-sofia&orientation=squarish' }, content: 'Creó una encuesta: ¿Qué día prefieren para el próximo meetup?', timestamp: 'Hace 15 horas', points: 10, targetTitle: 'Creadores Premium' },
];

/* ═══════════════════════════════════════════════════════════════════
   EXPANDED TOP MEMBERS — Top 10 / Top 50 / Top 100 (BLOQUE 26)
   ═══════════════════════════════════════════════════════════════════ */

const extraTopMembers: ClubMember[] = [
  { id: 2008, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20cheerful%20young%20latina%20woman%2C%20warm%20genuine%20smile%2C%20casual%20modern%20style%2C%20soft%20natural%20lighting%2C%20friendly%20expression%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background&width=200&height=200&seq=tm-maria&orientation=squarish', role: 'miembro', joinedDate: '2025-04-20', contributions: 980, score: 5100 },
  { id: 2009, name: 'Pedro López', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20friendly%20latino%20man%20in%20casual%20hoodie%2C%20relaxed%20smile%2C%20soft%20studio%20lighting%2C%20approachable%20modern%20vibe%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=tm-pedro&orientation=squarish', role: 'miembro', joinedDate: '2025-05-10', contributions: 870, score: 4600 },
  { id: 2010, name: 'Alejandra Molina', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20with%20curly%20hair%2C%20bright%20smile%2C%20colorful%20casual%20style%2C%20soft%20natural%20light%2C%20warm%20expression%2C%20clean%20headshot%20photography%2C%20neutral%20cream%20background&width=200&height=200&seq=tm-alejandra&orientation=squarish', role: 'miembro', joinedDate: '2025-07-05', contributions: 750, score: 4200 },
  { id: 2011, name: 'Ricardo Fonseca', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20latino%20man%20in%20his%20thirties%2C%20warm%20professional%20look%2C%20neat%20casual%20style%2C%20friendly%20confident%20smile%2C%20clean%20studio%20headshot%2C%20neutral%20background&width=200&height=200&seq=tm-ricardo&orientation=squarish', role: 'moderador', joinedDate: '2025-02-28', contributions: 1200, score: 6800 },
  { id: 2012, name: 'Lucía Fernández', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20with%20warm%20eyes%2C%20elegant%20simple%20style%2C%20natural%20makeup%2C%20genuine%20expression%2C%20clean%20studio%20headshot%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=tm-lucia&orientation=squarish', role: 'miembro', joinedDate: '2025-08-15', contributions: 680, score: 3800 },
  { id: 2013, name: 'Andrés Jiménez', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20latino%20man%20with%20glasses%2C%20intellectual%20look%2C%20warm%20smile%2C%20smart%20casual%20style%2C%20clean%20studio%20headshot%2C%20neutral%20background&width=200&height=200&seq=tm-andresj&orientation=squarish', role: 'miembro', joinedDate: '2025-09-01', contributions: 620, score: 3500 },
  { id: 2014, name: 'Paula Restrepo', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20with%20short%20hair%2C%20confident%20modern%20look%2C%20warm%20genuine%20smile%2C%20clean%20studio%20headshot%2C%20neutral%20cream%20background&width=200&height=200&seq=tm-paula&orientation=squarish', role: 'miembro', joinedDate: '2025-10-10', contributions: 590, score: 3200 },
  { id: 2015, name: 'Miguel Sarmiento', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20latino%20man%20in%20his%20forties%2C%20distinguished%20salt%20and%20pepper%20hair%2C%20warm%20fatherly%20smile%2C%20casual%20elegant%20style%2C%20clean%20studio%20headshot%2C%20neutral%20background&width=200&height=200&seq=tm-miguel&orientation=squarish', role: 'moderador', joinedDate: '2025-01-20', contributions: 1600, score: 8500 },
  { id: 2016, name: 'Fernanda Castillo', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latina%20woman%20with%20long%20dark%20hair%2C%20bright%20energetic%20smile%2C%20trendy%20casual%20style%2C%20clean%20studio%20headshot%2C%20neutral%20warm%20background&width=200&height=200&seq=tm-fernanda&orientation=squarish', role: 'miembro', joinedDate: '2025-11-05', contributions: 540, score: 2900 },
  { id: 2017, name: 'Joaquín Herrera', avatarUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20latino%20man%2C%20athletic%20build%2C%20energetic%20smile%2C%20casual%20sporty%20look%2C%20clean%20studio%20headshot%2C%20neutral%20background&width=200&height=200&seq=tm-joaquin&orientation=squarish', role: 'top_contribuidor', joinedDate: '2025-06-15', contributions: 1100, score: 6100 },
];

export function getExpandedTopMembers(clubId: number, tier: 10 | 50 | 100): ClubMember[] {
  const club = getAllClubs().find(c => c.id === clubId);
  if (!club) return [];
  const baseMembers = club.topMembers.length > 0 ? club.topMembers : extraTopMembers.slice(0, 3);
  const allMembers = [...baseMembers, ...extraTopMembers];
  const limit = tier === 100 ? Math.min(tier, allMembers.length) : Math.min(tier, allMembers.length);
  return allMembers.slice(0, limit);
}

export const topMembersSummary = {
  totalAcrossAllClubs: 123350,
  top10AvgScore: 8500,
  top50AvgScore: 5200,
  top100AvgScore: 3100,
  maxScore: 22800,
};

/* ═══════════════════════════════════════════════════════════════════
   ALL CLUBS COMBINED
   ═══════════════════════════════════════════════════════════════════ */

export function getAllClubs(): RfwClub[] {
  return [...radioClubs, ...programClubs, ...hostClubs, ...premiumClubs];
}

export function getClubById(id: number): RfwClub | undefined {
  return getAllClubs().find(c => c.id === id);
}

export function getClubsByType(type: ClubType): RfwClub[] {
  return getAllClubs().filter(c => c.type === type);
}

export function getClubByRadioId(radioId: number): RfwClub | undefined {
  return radioClubs.find(c => c.id === radioId);
}

export function getTopClubs(limit: number = 5): RfwClub[] {
  return [...getAllClubs()].sort((a, b) => b.clubScore - a.clubScore).slice(0, limit);
}

export function getRisingClubs(limit: number = 5): RfwClub[] {
  return [...getAllClubs()].sort((a, b) => b.scoreChange - a.scoreChange).slice(0, limit);
}

/* ─── BLOQUE 26: Most Active, Trending, New Clubs ─── */
export function getMostActiveClubs(limit: number = 5): RfwClub[] {
  return [...getAllClubs()].sort((a, b) => b.weeklyScore - a.weeklyScore).slice(0, limit);
}

export function getTrendingClubs(limit: number = 5): RfwClub[] {
  return [...getAllClubs()].sort((a, b) => b.engagementRate - a.engagementRate).slice(0, limit);
}

export function getNewClubs(limit: number = 5): RfwClub[] {
  return [...getAllClubs()].sort((a, b) => new Date(b.foundedDate).getTime() - new Date(a.foundedDate).getTime()).slice(0, limit);
}

export function getClubMainActivity(clubId: number, limit: number = 10): ClubActivityItem[] {
  const club = getClubById(clubId);
  if (!club) return globalClubActivityFeed.slice(0, limit);
  return globalClubActivityFeed
    .filter(a => a.targetTitle === club.clubName)
    .slice(0, Math.max(3, limit));
}

/* ─── Summary Stats ─── */
export const clubsSummary = {
  totalClubs: 27,
  totalMembers: 126870,
  totalPosts: 250300,
  totalChallenges: 434,
  totalActiveChallenges: 24,
  totalEvents: 234,
  topClubName: 'La Mexicana Fan Club',
  topClubScore: 22800,
};